import { Typography, Link } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { Link as RouterLink } from "react-router";
import { orderService } from "../../../Core/Api/services";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../Components/Table/table";
import TableCellDate2Lines from "../../../Components/Table/TableCellDate2Lines";
import TableCellOwner2Lines from "../../../Components/Table/TableCellOwner2Lines/table-cell-owner-2-lines";
import TableCellOrderPriority from "../../../Components/Table/TableCellOrderPriority/table-cell-order-priority";
import TableCellDueDate from "../../../Components/Table/TableCellDueDate/TableCellDueDate";
import TableCellOrderStatus from "../../../Components/Table/TableCellOrderStatus/table-cell-order-status";
import { Stack } from "@mui/system";
import { routePaths } from "../../../Router/router";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import FullTaskTopbar from "../../../Components/Topbars/FullTaskTopbar";
import FullTaskLayout from "../../../Layouts/FullTask";
import { useTranslation } from "react-i18next";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";

function OrderFollowup() {
  const { t } = useTranslation();
  const params = useParams();
  type Row = DbOrderItemInfos[][number];
  const [rows, setRows] = useState<DbOrderItemInfos[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  let orderID: number = parseInt(params.orderId!);
  let key = params.key;

  useEffect(() => {
    orderService.GetOrderItemsForOrderId(orderID, key!).then((response) => {
      if (response.items) {
        setRows(response.items);
        setIsLoading(false);
      }
    });
  }, []);

  // columns
  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "ID",
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: GridRenderCellParams<Row>) => <span title={data.value}>{data.value}</span>,
    },
    {
      field: "creationTime",
      headerName: t("common:creationDateLowerCase"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: GridRenderCellParams<Row>) => <TableCellDate2Lines date={data.value} />,
    },
    {
      field: "endTime",
      headerName: t("table:headerName.orderFollowupEndDate"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueGetter: (_, row: Row) => row.endTime,
      renderCell: (data: GridRenderCellParams<Row>) => {
        return data.value === null ? (
          <Typography variant="body2" color="text.tier">
            -
          </Typography>
        ) : (
          <TableCellDate2Lines date={data.value} />
        );
      },
    },
    {
      field: "ownerUserName",
      headerName: t("common:owner"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: GridRenderCellParams<Row>) => (
        <TableCellOwner2Lines userName={data.row.ownerUserName ?? "—"} groupName={data.row.ownerUserGroupName ?? "—"} />
      ),
    },
    {
      field: "objectName",
      headerName: t("common:title"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: GridRenderCellParams<Row>) => (
        <Typography title={data.value} variant="body2">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "summarizedInfos",
      headerName: t("table:headerName.summarizedInfos"),
      flex: CellSize.xlarge,
      minWidth: CellSize.xlarge,
      renderCell: (data: GridRenderCellParams<Row>) => (
        <Typography sx={{ whiteSpace: "break-spaces" }} variant="body2" color="text.tier">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "priority",
      headerName: t("common:priority"),
      flex: CellSize.small,
      minWidth: CellSize.small,
      renderCell: (data: GridRenderCellParams<Row>) => <TableCellOrderPriority priority={data.value} />,
    },
    {
      field: "status",
      headerName: t("common:status"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: GridRenderCellParams<Row>) => TableCellOrderStatus({ data: data }),
    },
    {
      field: "message",
      headerName: t("common:message"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: GridRenderCellParams<Row>) => (
        <Typography variant="body2" color="text.tier">
          {data.value || "-"}
        </Typography>
      ),
    },
    {
      field: "Download",
      headerName: t("common:downloadLink"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: GridRenderCellParams<Row>) => {
        return !data?.row?.fileTransfer ? (
          <Typography variant="body2" color="text.tier">
            {t("table:renderCell.noTransferLink")}
          </Typography>
        ) : (
          <Link
            to={`${routePaths.download.path}/${data?.row?.fileTransfer.key}`}
            color="primary.text"
            underline="hover"
            component={RouterLink}
            target="_blank"
          >
            {t("common:download")}
          </Link>
        );
      },
    },
    {
      field: "expire",
      headerName: t("common:expireDate"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      valueGetter: (_, row: Row) => row.fileTransfer?.expireDate,
      renderCell: (data: GridRenderCellParams<Row>) => TableCellDueDate({ data }),
    },
  ];

  return (
    <FullTaskLayout TopBar={<FullTaskTopbar title={t("page:fullTaskLayout.title.orderFollowUp")} />}>
      <Stack sx={{ padding: "50px", width: "100vw", mt: 5 }}>
        <Typography variant="body1" color="text.primary" sx={{ overflowWrap: "break-word" }}>
          {t("page:body.orderFollowUpWithId", { id: orderID })}
        </Typography>
        <Typography variant="body2" color="text.tier" sx={{ overflowWrap: "break-word", mb: 2 }}>
          {t("page:body.seeAllItemsLinkedToOrder")}
        </Typography>
        {isLoading && <TableSkeleton />}
        {!isLoading && (
          <DataTable
            noControls
            sx={{ width: "100%" }}
            rows={rows}
            columns={columns}
            columnHeaderHeight={48}
            rowHeight={56}
            pageSize={100}
            getRowId={(row: any) => row.id}
            getRowHeight={() => "auto"}
            disableRowSelectionOnClick
            initialState={{
              pinnedColumns: { left: ["id"], right: ["Download"] },
            }}
          />
        )}
      </Stack>
    </FullTaskLayout>
  );
}

export default OrderFollowup;
